import { Select } from '@/design-system';
import type { PublicLanguage, PublicTenant } from '@/entities/publicReport';
import { cn } from '@/shared/lib';

import { availableLanguages, LANGUAGE_LABELS, type PublicCopy } from '../copy';
import { emailHref, phoneHref, websiteHref, websiteLabel } from '../format';

export interface CompanyBarProps {
    tenant: PublicTenant;
    language: PublicLanguage;
    onLanguageChange: (language: PublicLanguage) => void;
    copy: PublicCopy;
}

/**
 * De kop van het rapport, in de huisstijl van het keuringsbedrijf: links het
 * logo, groot genoeg om het bedrijf aan te herkennen, en rechts op dezelfde rij
 * al zijn gegevens. Smartkeur staat nergens op de pagina. Lucas: "Kan boven aan
 * rapport logo wat groter en bedrijfsgegevens rechts" - en daarna: alle
 * bedrijfsgegevens, ook adres, e-mail en website.
 *
 * Het logo is 60 px hoog en hooguit 240 px breed, op een telefoon 48 en 200, en
 * houdt altijd zijn eigen verhouding: een heel breed logo wordt lager, nooit
 * platgedrukt. Zonder logo staat de naam links: een leeg vlak zou de koper een
 * onaf scherm laten zien.
 *
 * De gegevens staan in een `<address>`, als op briefpapier: de naam, het adres
 * zoals het rapport het drukt (straat, dan postcode en plaats; één regel als er
 * maar één deel is), dan telefoon, e-mail en website, en de openingstijden.
 * Telefoon is een `tel:`-link en e-mail een `mailto:`; de website staat er zonder
 * `https://` en `www.` en opent de echte url in een nieuw tabblad, zodat de koper
 * zijn rapport niet kwijt is. Wat het bedrijf niet invulde, staat er niet: nooit
 * een lege regel.
 *
 * Elke link is een tikvlak van 44 px, zoals alles wat je op deze pagina aanraakt.
 * Daarom staan telefoon, e-mail en website naast elkaar en niet onder elkaar:
 * drie rijen van 44 px zouden de kop twee keer zo hoog maken. Passen ze niet op
 * één regel, dan loopt de rest door op de volgende.
 *
 * Vanaf 480 px blijven de gegevens naast het logo staan en lopen ze desnoods
 * over meer regels; op een telefoon staan ze eronder, links uitgelijnd zoals het
 * logo, als ze er niet naast passen. Een lang e-mailadres of een lange website
 * breekt af in plaats van de pagina breder te maken.
 *
 * De gegevens zijn de bedrijfsgegevens uit de instellingen, dezelfde als op het
 * rapport en de factuur (`App\Domain\Tenancy\Data\TenantContact`). De koper, de
 * kantoorpreview en "Zoals de koper het ziet" tekenen allemaal deze kop.
 */
export function CompanyBar({ tenant, language, onLanguageChange, copy }: CompanyBarProps) {
    const languages = availableLanguages(tenant.languages);
    const logo = tenant.logoUrl;
    const contacts = contactsOf(tenant);

    return (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3 border-b border-[var(--border-subtle)] px-[var(--pr-pad-x)] py-[18px]">
            {logo ? (
                <img
                    src={logo}
                    alt={tenant.name}
                    className="block h-12 w-auto max-w-[200px] shrink-0 rounded-[5px] object-contain @min-[480px]:h-[60px] @min-[480px]:max-w-[240px]"
                />
            ) : null}

            <address
                className={cn(
                    'min-w-0 text-[13px] leading-[18px] break-words text-[var(--text-secondary)] not-italic',
                    logo &&
                        '@min-[480px]:ml-auto @min-[480px]:min-w-[160px] @min-[480px]:flex-[1_1_0%] @min-[480px]:text-right',
                )}
            >
                <div className="text-[14px] leading-[1.3] font-medium text-[var(--text-primary)]">
                    {tenant.name}
                </div>
                {tenant.addressLines.map((line, index) => (
                    <div key={index} className={index === 0 ? 'mt-[3px]' : undefined}>
                        {line}
                    </div>
                ))}
                {contacts.length > 0 ? (
                    <div
                        className={cn('flex flex-wrap gap-x-4', logo && '@min-[480px]:justify-end')}
                    >
                        {contacts.map((contact) => (
                            <ContactLink key={contact.label} {...contact} />
                        ))}
                    </div>
                ) : null}
                {tenant.openingHours ? (
                    <div className="mt-px text-[12px] leading-[1.35] text-[var(--text-tertiary-safe)]">
                        {tenant.openingHours}
                    </div>
                ) : null}
            </address>

            {languages.length > 1 ? (
                <Select
                    size="md"
                    aria-label={copy.languageLabel}
                    value={language}
                    className={cn('w-auto min-w-[136px]', !logo && 'ml-auto')}
                    onChange={(event) => onLanguageChange(event.target.value as PublicLanguage)}
                >
                    {languages.map((option) => (
                        <option key={option} value={option} lang={option}>
                            {`${option.toUpperCase()} · ${LANGUAGE_LABELS[option]}`}
                        </option>
                    ))}
                </Select>
            ) : null}
        </div>
    );
}

interface Contact {
    label: string;
    href: string | null;
    /** Een website opent in een nieuw tabblad, zonder de link van het rapport mee te geven. */
    external?: boolean;
}

/** Telefoon, e-mail en website, in die volgorde; wat het bedrijf niet invulde, valt weg. */
function contactsOf(tenant: PublicTenant): Contact[] {
    const contacts: Contact[] = [];

    if (tenant.phone) {
        contacts.push({ label: tenant.phone, href: phoneHref(tenant.phone) });
    }

    if (tenant.email) {
        contacts.push({ label: tenant.email, href: emailHref(tenant.email) });
    }

    if (tenant.website) {
        contacts.push({
            label: websiteLabel(tenant.website),
            href: websiteHref(tenant.website),
            external: true,
        });
    }

    return contacts;
}

/**
 * 44 px hoog met een regel van 18 px: 13 px erboven en eronder. Een blok en geen
 * `inline-flex`, zodat een lang e-mailadres binnen de regel afbreekt.
 */
const CONTACT_BOX = 'block min-h-[44px] max-w-full min-w-0 py-[13px]';

/**
 * Bellen, mailen of kijken, in de stijl van de andere links op de pagina. Zonder
 * bruikbare link staat de waarde er als tekst: een gegeven tonen is beter dan het
 * weglaten.
 */
function ContactLink({ label, href, external = false }: Contact) {
    if (!href) {
        return <span className={CONTACT_BOX}>{label}</span>;
    }

    return (
        <a
            href={href}
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            className={cn(
                CONTACT_BOX,
                'rounded-[2px] text-[var(--accent-dark)] underline decoration-[var(--accent-border)] underline-offset-2 hover:decoration-current focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--accent)]',
            )}
        >
            {label}
        </a>
    );
}
